import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { loginRequestSchema, type LoginRequest } from "@nh/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  AuthCard,
  AuthLayout,
  EmailField,
  Form,
  FormError,
  PasswordField,
  SubmitButton,
} from "@/shared/ui/core";
import { useAuth } from "./auth.context";
import { isEmailNotVerified } from "./auth.errors";
import { destinationForRole } from "./auth.routes";
import { AuthSwitchLink } from "./components/AuthSwitchLink";
import { ForgotPasswordLink } from "./components/ForgotPasswordLink";
import { GoogleSection } from "./components/GoogleSection";
import { UnverifiedEmailNotice } from "./components/UnverifiedEmailNotice";
import { useAuthSubmit } from "./useAuthSubmit";

/** Lo que otra pantalla puede dejar dicho al mandar a alguien aquí. */
interface LoginNavigationState {
  /** La ruta protegida de la que se le echó, para devolverlo ahí. */
  from?: string;
  /** El correo recién verificado, para no hacérselo escribir otra vez. */
  email?: string;
  /**
   * `true` cuando se llega desde el registro recién completado: la
   * pantalla da la bienvenida y pide entrar, en vez de recibir en seco
   * a alguien que acaba de escribir sus datos completos.
   */
  justRegistered?: boolean;
}

export function LoginPage() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as LoginNavigationState | null;

  const form = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    // Quien acaba de verificar su correo llega con él puesto: es lo que
    // `verifyEmailResponseSchema` devuelve el correo PARA.
    defaultValues: { email: navigationState?.email ?? "", password: "" },
    mode: "onSubmit",
  });

  /**
   * El correo de la cuenta sin verificar, o `null`.
   *
   * Es lo que decide si se ofrece el reenvío del enlace en vez del aviso
   * genérico. Guarda el correo YA NORMALIZADO del último intento —no lo
   * que hay escrito en el campo ahora mismo— para que el reenvío vaya a la
   * dirección con la que el servidor acaba de quejarse.
   */
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const attempted = useRef("");

  /*
   * El rol de quien acaba de entrar, para decidir su destino. Se captura
   * en la acción porque `onSuccess` de `useAuthSubmit` no recibe el
   * resultado.
   */
  const loggedRole = useRef<string | undefined>(undefined);

  const { submit, formError, dismissFormError } = useAuthSubmit({
    form,
    fields: ["email", "password"],
    action: async (values) => {
      attempted.current = values.email;
      const user = await login(values);
      loggedRole.current = user.role;
      return user;
    },
    /*
     * `EMAIL_NOT_VERIFIED` es el único fallo del login con una salida
     * concreta, y por eso no cae en el banner genérico.
     *
     * Dejarlo caer ahí sería lo peor que puede hacer esta pantalla: quien
     * lea "algo salió mal" va a probar su contraseña otra vez, y otra, con
     * una contraseña que está perfectamente bien, hasta que el servidor le
     * bloquee la cuenta por demasiados intentos.
     *
     * ⚠️ Hoy la compuerta que exige el correo verificado nace APAGADA en el
     * servidor (ver el comentario de verificación en `@nh/shared`), así que
     * este camino no se dispara todavía en vivo. Está probado con la
     * respuesta simulada, y el día que se encienda funciona sin tocar nada.
     */
    onError: (error) => {
      if (!isEmailNotVerified(error)) return false;
      setUnverifiedEmail(attempted.current);
      return true;
    },
    onSuccess: () => {
      setUnverifiedEmail(null);
      /*
       * Si alguien llegó aquí porque una ruta protegida lo mandó, se le
       * devuelve a donde iba. Sin `from`, el destino lo decide su ROL:
       * quien administra entra a su panel, quien compra a la tienda —
       * entrar y aterrizar en una pantalla que no reconoce tu papel es
       * la mala experiencia que Anahí señaló (26-ago). `replace` para
       * que el botón de atrás no regrese al login ya resuelto.
       */
      navigate(navigationState?.from ?? destinationForRole(loggedRole.current), {
        replace: true,
      });
    },
  });

  /*
   * ── Con sesión, esta pantalla NO se enseña (Anahí, 27-ago) ──────────
   * Llegar al login ya estando dentro pintaba el formulario como si la
   * sesión no existiera — "me dice eso cuando sí tengo el inicio de
   * sesión". Quien ya entró sigue de largo: a donde venía (`from`) o a
   * donde le toca por rol.
   */
  if (isAuthenticated) {
    return (
      <Navigate to={navigationState?.from ?? destinationForRole(user?.role)} replace />
    );
  }

  return (
    <AuthLayout>
      <AuthCard
        title="¡Bienvenido!"
        footer={
          <AuthSwitchLink
            prompt="¿No tienes una cuenta?"
            linkLabel="Crea una"
            to="/register"
            // El `from` sigue viajando: quien vino del carrito y decide
            // registrarse también tiene que volver al carrito.
            state={navigationState ?? undefined}
          />
        }
      >
        {/*
          `key` para que, si se reintenta con OTRO correo y el servidor
          vuelve a quejarse, el formulario de reenvío nazca de nuevo con la
          dirección nueva en vez de quedarse con la anterior.
        */}
        {/*
          La bienvenida de quien acaba de registrarse: sin ella, el
          brinco registro → login se siente como un error ("¿no me
          registré ya?"). Dice qué pasó y qué sigue.
        */}
        {navigationState?.justRegistered && (
          <Alert variant="success" title="¡Tu cuenta se creó!">
            Inicia sesión con tu correo y tu contraseña para continuar.
          </Alert>
        )}

        {unverifiedEmail && (
          <UnverifiedEmailNotice key={unverifiedEmail} email={unverifiedEmail} />
        )}

        <FormError error={formError} onDismiss={dismissFormError} />

        <Form form={form} onSubmit={submit} className="grid gap-4">
          <EmailField purpose="account" autoFocus />
          <PasswordField purpose="current" labelAction={<ForgotPasswordLink />} />
          <SubmitButton size="lg" shape="rounded" fullWidth>
            Iniciar sesión
          </SubmitButton>
        </Form>

        {/* Entrar con Google respeta el mismo destino que el login normal. */}
        <GoogleSection
          disabled={form.formState.isSubmitting}
          destination={navigationState?.from}
        />
      </AuthCard>
    </AuthLayout>
  );
}
